import { useAlert } from 'components/alert'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import {
  assinaturaDigitalCancelMessage,
  assinaturaDigitalSuccessMessage,
  defaultAssinaturaDigitalErrorMessage,
  DocumentoDigitalErrorCodeEnum,
  documentoDigitalErrorCodeToMessage,
  EmitirBundleFhirDigitalPopupInput,
  StatusAssinaturaDigitalPopup,
  TipoDocumentoAssinaturaDigitalPopup,
} from 'hooks/assinatura-digital/model-assinaturaDigitalPopup'
import { useAssinaturaDigitalPopup } from 'hooks/assinatura-digital/useAssinaturaDigitalPopup'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'

import { BundleFhirDigitalSituacaoEnum } from '../../model-prescricao'
import { BundleFhirAssinatura } from './AssinadorRepmModal'

interface BundleFhirDigitalPopupProps {
  envioRndsId: ID
  atendimentoId: ID
  setBundlesFhirAssinatura: Dispatch<SetStateAction<BundleFhirAssinatura[]>>
}

interface BundleFhirDigitalPopupResult {
  handleAssinar: () => Promise<void>
  isAssinado: boolean
  isAssinando: boolean
  situacao: BundleFhirDigitalSituacaoEnum
}

export function useBundleFhirDigitalPopup(props: BundleFhirDigitalPopupProps): BundleFhirDigitalPopupResult {
  const { atendimentoId, envioRndsId, setBundlesFhirAssinatura } = props

  const popupDataIdentifierRef = useRef<string | null>(null)
  const [isAssinando, setIsAssinando] = useState(false)
  const [situacao, setSituacao] = useState<BundleFhirDigitalSituacaoEnum>(BundleFhirDigitalSituacaoEnum.PENDENTE)
  const { analytics } = useAnalytics()
  const alert = useAlert()

  const { data: popupData, openPopup, closePopup } = useAssinaturaDigitalPopup<EmitirBundleFhirDigitalPopupInput>(
    { envioRndsId, atendimentoId },
    TipoDocumentoAssinaturaDigitalPopup.BUNDLE_FHIR
  )

  const handleError = useCallback(
    (error?: DocumentoDigitalErrorCodeEnum) => {
      setIsAssinando(false)
      setSituacao(BundleFhirDigitalSituacaoEnum.ERRO)
      alert('danger', error ? documentoDigitalErrorCodeToMessage[error] : defaultAssinaturaDigitalErrorMessage)
    },
    [alert]
  )

  const handleCancel = useCallback(() => {
    setIsAssinando(false)
    alert('danger', assinaturaDigitalCancelMessage)
  }, [alert])

  const handleSuccess = useCallback(() => {
    setBundlesFhirAssinatura((bundles) =>
      bundles.map((bundle) => (bundle.id === envioRndsId ? { ...bundle, assinado: true } : bundle))
    )

    setIsAssinando(false)
    setSituacao(BundleFhirDigitalSituacaoEnum.ASSINADO)
    alert('success', assinaturaDigitalSuccessMessage)
  }, [alert, envioRndsId, setBundlesFhirAssinatura])

  const handleAssinar = async () => {
    analytics.logEvent('click_gerar_repm_digital')
    try {
      setIsAssinando(true)
      await openPopup({ envioRndsId, atendimentoId })
    } catch {
      handleError(popupData?.error)
    }
  }

  useEffect(() => {
    if (isAssinando && popupData?.identifier !== popupDataIdentifierRef.current) {
      switch (popupData?.status) {
        case StatusAssinaturaDigitalPopup.SUCCESS:
          handleSuccess()
          break
        case StatusAssinaturaDigitalPopup.CANCELED:
          handleCancel()
          break
        case StatusAssinaturaDigitalPopup.FAILURE:
          handleError(popupData?.error)
          break
      }
    }
    popupDataIdentifierRef.current = popupData?.identifier
  }, [handleCancel, handleError, handleSuccess, isAssinando, popupData, popupDataIdentifierRef])

  useEffect(() => {
    if (!isAssinando) {
      closePopup()
    }
  }, [isAssinando, closePopup])

  const isAssinado = situacao === BundleFhirDigitalSituacaoEnum.ASSINADO

  return {
    handleAssinar,
    isAssinado,
    isAssinando,
    situacao,
  }
}
